// Google Places for the v2 address field: debounced predictions while the
// customer types their street address, and a full parse when they pick
// one. Extracted from the v1 LocationField's proven wiring — including the
// session counter that keeps a slow early response from overwriting newer
// results. State comes back as the two-letter abbreviation.

import { useEffect, useRef, useState } from "react";

import { loadGoogleMaps } from "helpers/googleMaps";

export interface PlaceSuggestion {
  placeId: string;
  description: string;
}

export interface ParsedAddress {
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  zip?: string;
}

function component(
  components: google.maps.GeocoderAddressComponent[],
  type: string,
): google.maps.GeocoderAddressComponent | undefined {
  return components.find((candidate) => candidate.types.includes(type));
}

export function usePlacesAutocomplete() {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [apiLoaded, setApiLoaded] = useState(false);
  const autocompleteRef = useRef<google.maps.places.AutocompleteService | null>(
    null,
  );
  const placesRef = useRef<google.maps.places.PlacesService | null>(null);
  const debounceRef = useRef<number | null>(null);
  const sessionRef = useRef(0);

  useEffect(() => {
    let mounted = true;
    loadGoogleMaps()
      .then(() => {
        if (!mounted) return;
        autocompleteRef.current = new google.maps.places.AutocompleteService();
        placesRef.current = new google.maps.places.PlacesService(
          document.createElement("div"),
        );
        setApiLoaded(true);
      })
      // The grid still works typed by hand; autocomplete is a convenience.
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const dismiss = () => {
    sessionRef.current += 1;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    setSuggestions([]);
  };

  const onStreetInput = (value: string) => {
    if (!apiLoaded) return;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (value.length < 4) {
      dismiss();
      return;
    }
    sessionRef.current += 1;
    const session = sessionRef.current;
    debounceRef.current = window.setTimeout(() => {
      autocompleteRef.current?.getPlacePredictions(
        { input: value, componentRestrictions: { country: "us" } },
        (predictions, status) => {
          if (sessionRef.current !== session) return;
          if (
            status === google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(
              predictions.slice(0, 5).map((prediction) => ({
                placeId: prediction.place_id,
                description: prediction.description,
              })),
            );
          } else {
            setSuggestions([]);
          }
        },
      );
    }, 400);
  };

  const selectSuggestion = (
    placeId: string,
    onParsed: (address: ParsedAddress) => void,
  ) => {
    dismiss();
    placesRef.current?.getDetails(
      { placeId, fields: ["address_components"] },
      (place, status) => {
        if (
          status !== google.maps.places.PlacesServiceStatus.OK ||
          !place?.address_components
        ) {
          return;
        }
        const components = place.address_components;
        const streetNumber = component(components, "street_number");
        const route = component(components, "route");
        // Rural and unincorporated places often carry no `locality`; take
        // the nearest thing Google offers instead of leaving city blank.
        const city =
          component(components, "locality") ??
          component(components, "postal_town") ??
          component(components, "sublocality_level_1") ??
          component(components, "sublocality") ??
          component(components, "administrative_area_level_3");
        const parsed: ParsedAddress = {
          address_line_1:
            streetNumber && route
              ? `${streetNumber.long_name} ${route.long_name}`
              : route?.long_name,
          address_line_2: component(components, "subpremise")?.long_name,
          city: city?.long_name,
          state: component(components, "administrative_area_level_1")
            ?.short_name,
          zip: component(components, "postal_code")?.long_name,
        };
        onParsed(parsed);
      },
    );
  };

  return { suggestions, onStreetInput, selectSuggestion, dismiss };
}
